import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Calendar, Clock, User, ArrowRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { ARTICLES } from './RecursosPage';

const fade = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0 } };

/* Prose helpers */
export const H2 = ({ children }) => (
  <h2 className="text-white font-black text-2xl sm:text-3xl mt-12 mb-4 leading-snug">{children}</h2>
);
export const H3 = ({ children }) => (
  <h3 className="text-white font-bold text-lg sm:text-xl mt-8 mb-3 leading-snug">{children}</h3>
);
export const P = ({ children }) => (
  <p className="text-slate-300 text-base leading-relaxed mb-5">{children}</p>
);
export const Li = ({ children }) => (
  <li className="flex items-start gap-3 text-slate-300 text-sm leading-relaxed mb-2">
    <span className="w-1.5 h-1.5 rounded-full bg-cyan-400 flex-shrink-0 mt-2" />
    {children}
  </li>
);
export const Ul = ({ children }) => <ul className="mb-5 space-y-0.5">{children}</ul>;
export const Blockquote = ({ children, source }) => (
  <blockquote className="my-7 pl-5 border-l-2 border-cyan-400/50">
    <p className="text-slate-200 text-base italic leading-relaxed mb-1">"{children}"</p>
    {source && <p className="text-[11px] text-slate-500 font-semibold uppercase tracking-widest">{source}</p>}
  </blockquote>
);
export const DataBox = ({ stat, label, source, color = 'cyan' }) => {
  const colorMap = {
    cyan:   'border-cyan-400/30 bg-cyan-400/8 text-cyan-300',
    red:    'border-red-400/30 bg-red-400/8 text-red-300',
    violet: 'border-violet-400/30 bg-violet-400/8 text-violet-300',
    amber:  'border-amber-400/30 bg-amber-400/8 text-amber-300',
  };
  return (
    <div className={`my-6 rounded-2xl border p-5 ${colorMap[color]}`}>
      <p className="text-2xl font-black mb-1">{stat}</p>
      <p className="text-sm text-slate-300 mb-1">{label}</p>
      {source && <p className="text-[10px] text-slate-500 font-bold uppercase tracking-widest">{source}</p>}
    </div>
  );
};

export default function ArticleLayout({ meta, children, cta }) {
  const others = ARTICLES.filter(a => a.slug !== meta.slug);

  return (
    <div className="min-h-screen bg-[#02060f]" data-testid={`article-${meta.slug}`}>
      {/* Back nav */}
      <div className="pt-24 pb-4 border-b border-white/6 bg-[#02060f]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/recursos" className="inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white transition-colors" data-testid={`article-back-${meta.slug}`}>
            <ArrowLeft className="w-4 h-4" /> Volver a Recursos
          </Link>
        </div>
      </div>

      {/* Article header */}
      <header className="pt-10 pb-10 bg-[#02060f]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div variants={{ hidden: {}, show: { transition: { staggerChildren: 0.07 } } }} initial="hidden" animate="show">
            <motion.div variants={fade} className="flex flex-wrap items-center gap-3 mb-5">
              <span className={`text-[10px] font-black uppercase tracking-widest border rounded-full px-3 py-1 ${meta.tagColor}`}>
                {meta.tag}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <Calendar className="w-3 h-3" /> {meta.date}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <Clock className="w-3 h-3" /> {meta.readTime}
              </span>
              <span className="flex items-center gap-1.5 text-[11px] text-slate-500">
                <User className="w-3 h-3" /> {meta.author}
              </span>
            </motion.div>
            <motion.h1 variants={fade} className="text-white font-heading text-3xl sm:text-4xl font-black leading-tight mb-5" data-testid={`article-title-${meta.slug}`}>
              {meta.title}
            </motion.h1>
            <motion.p variants={fade} className="text-slate-300 text-lg leading-relaxed border-l-2 pl-4" style={{ borderColor: meta.accent }}>
              {meta.excerpt}
            </motion.p>
          </motion.div>
        </div>
      </header>

      {/* Article body */}
      <article className="pb-16 bg-[#030a17] border-t border-white/6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 pt-12">
          {children}
        </div>
      </article>

      {/* CTA */}
      {cta && (
        <section className="py-16 bg-[#02060f] border-t border-white/6" data-testid={`article-cta-${meta.slug}`}>
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <motion.div variants={{ hidden: {}, show: { transition: { staggerChildren: 0.08 } } }} initial="hidden" whileInView="show" viewport={{ once: true }}>
              <motion.h2 variants={fade} className="text-white font-black text-2xl sm:text-3xl mb-4">{cta.title}</motion.h2>
              <motion.p variants={fade} className="text-slate-400 text-base mb-7 max-w-xl mx-auto">{cta.desc}</motion.p>
              <motion.div variants={fade} className="flex flex-wrap justify-center gap-4">
                <Link to={cta.href} className="btn-primary" data-testid={`article-cta-btn-${meta.slug}`}>
                  {cta.label} <ArrowRight className="w-4 h-4" />
                </Link>
                {cta.secondary && (
                  <Link to={cta.secondary.href} className="btn-secondary border-white/15 text-slate-200 hover:bg-white/5">
                    {cta.secondary.label}
                  </Link>
                )}
              </motion.div>
            </motion.div>
          </div>
        </section>
      )}

      {/* Related articles */}
      <section className="py-12 bg-[#030b18] border-t border-white/6">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs font-bold uppercase tracking-widest text-slate-500 mb-5">También te puede interesar</p>
          <div className="grid sm:grid-cols-2 gap-4">
            {others.map(art => (
              <Link key={art.slug} to={`/recursos/${art.slug}`}
                className="group rounded-xl border border-white/8 bg-white/[0.02] p-5 hover:border-white/16 hover:bg-white/[0.04] transition-all"
                data-testid={`article-related-${art.slug}`}>
                <span className={`text-[9px] font-black uppercase tracking-widest border rounded-full px-2 py-0.5 ${art.tagColor}`}>{art.tag}</span>
                <p className="text-white text-sm font-bold mt-3 mb-2 leading-snug group-hover:text-cyan-200 transition-colors line-clamp-2">{art.title}</p>
                <p className="text-[11px] text-slate-400">{art.readTime}</p>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
